// ============================================================================
// WORK ITEMS FILTER HOOK
// Centralized search, filter, and sort logic for all work item views
// Date: December 2025
// ============================================================================

'use client';

import { useState, useMemo, useCallback } from 'react';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { invoiceRowIsRecurring } from '@/lib/finance/invoiceListUi';

// ============================================================================
// TYPES
// ============================================================================

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface FilterConfig {
  search?: string;
  status?: string[];
  priority?: string[];
  itemType?: string[];
  itemSubtype?: string[];
  assignedTo?: (number | null)[];
  tags?: string[];
  dateRange?: {
    field: 'created_at' | 'updated_at' | 'due_date' | 'start_date';
    start?: Date;
    end?: Date;
  };
  customFilters?: Record<string, unknown>;
}

export type WorkItemStatFilter = 'open' | 'urgent' | 'in_progress' | 'overdue' | 'unassigned' | 'on_hold';

export interface FilterPreset {
  id: string;
  name: string;
  filters: FilterConfig;
  sorts: SortConfig[];
  isDefault?: boolean;
  icon?: string;
}

export interface UseWorkItemsFilterOptions {
  items: WorkItem[];
  defaultFilters?: FilterConfig;
  defaultSort?: SortConfig[];
  onFilterChange?: (filters: FilterConfig) => void;
  onSortChange?: (sorts: SortConfig[]) => void;
  /** When true, text search is not applied client-side (API already filtered by search). */
  serverSideSearch?: boolean;
  /** When set, overrides stats.total (e.g. API pagination total across all pages). */
  serverResultTotal?: number | null;
}

export interface UseWorkItemsFilterReturn {
  // Filtered and sorted items
  filteredItems: WorkItem[];
  
  // Filter state
  filters: FilterConfig;
  setFilters: (filters: FilterConfig) => void;
  updateFilter: <K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  
  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  
  // Sort state
  sorts: SortConfig[];
  setSorts: (sorts: SortConfig[]) => void;
  toggleSort: (field: string) => void;
  clearSorts: () => void;
  
  // Presets
  presets: FilterPreset[];
  applyPreset: (presetId: string) => void;
  savePreset: (name: string, icon?: string) => FilterPreset;
  
  // Quick filters
  quickFilters: {
    myItems: () => void;
    overdue: () => void;
    highPriority: () => void;
    recentlyUpdated: () => void;
    unassigned: () => void;
  };
  isHighPriorityActive: boolean;
  
  // Stats
  stats: {
    total: number;
    filtered: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    byType: Record<string, number>;
    bySubtype: Record<string, number>;
  };
}

const HIGH_PRIORITY_VALUES: string[] = ['high', 'urgent', 'critical'];

// ============================================================================
// DEFAULT PRESETS
// ============================================================================

const DEFAULT_PRESETS: FilterPreset[] = [
  {
    id: 'all',
    name: 'All Items',
    filters: {},
    sorts: [{ field: 'created_at', direction: 'desc' }],
    isDefault: true,
    icon: '📋'
  },
  {
    id: 'my-items',
    name: 'My Items',
    filters: { assignedTo: [] }, // Will be populated with current user ID
    sorts: [{ field: 'due_date', direction: 'asc' }],
    icon: '👤'
  },
  {
    id: 'high-priority',
    name: 'High Priority',
    filters: { priority: ['high', 'urgent', 'critical'] },
    sorts: [{ field: 'priority', direction: 'desc' }, { field: 'due_date', direction: 'asc' }],
    icon: '🔥'
  },
  {
    id: 'overdue',
    name: 'Overdue',
    filters: {}, // Handled by date comparison
    sorts: [{ field: 'due_date', direction: 'asc' }],
    icon: '⚠️'
  },
  {
    id: 'recently-updated',
    name: 'Recently Updated',
    filters: {},
    sorts: [{ field: 'updated_at', direction: 'desc' }],
    icon: '🕐'
  }
];

// ============================================================================
// SORT FIELD CONFIGS
// ============================================================================

export const SORTABLE_FIELDS = [
  { field: 'item_number', label: 'Item Number' },
  { field: 'title', label: 'Title' },
  { field: 'status', label: 'Status' },
  { field: 'priority', label: 'Priority' },
  { field: 'assigned_to', label: 'Assignee' },
  { field: 'due_date', label: 'Due Date' },
  { field: 'start_date', label: 'Start Date' },
  { field: 'created_at', label: 'Created' },
  { field: 'updated_at', label: 'Updated' },
  { field: 'progress_percentage', label: 'Progress' },
  { field: 'estimated_hours', label: 'Estimated Hours' },
  { field: 'actual_hours', label: 'Actual Hours' },
  // Inventory entity preset sortable fields (read from custom_fields)
  { field: 'from_warehouse', label: 'From Warehouse' },
  { field: 'to_warehouse', label: 'To Warehouse' },
  { field: 'transfer_type', label: 'Transfer Type' },
  { field: 'expected_arrival_date', label: 'Expected Arrival' },
  { field: 'product_name', label: 'Product' },
  { field: 'warehouse_name', label: 'Warehouse' },
  { field: 'quantity', label: 'Quantity' },
  { field: 'fulfilled_quantity', label: 'Fulfilled Qty' },
  { field: 'expires_at', label: 'Expires' },
  { field: 'entity_type', label: 'Entity Type' },
  { field: 'entity_id', label: 'Entity' },
  { field: 'amount', label: 'Amount' },
  { field: 'approvals_count', label: 'Approvals' },
  { field: 'requested_by_name', label: 'Requested By' },
  { field: 'list_type', label: 'List Type' },
  { field: 'currency', label: 'Currency' },
  { field: 'pricing_method', label: 'Pricing Method' },
  { field: 'item_count', label: 'Item Count' },
  { field: 'customer_count', label: 'Customer Count' },
  { field: 'valid_from', label: 'Valid From' },
  { field: 'valid_to', label: 'Valid To' },
  { field: 'count_type', label: 'Count Type' },
  { field: 'scheduled_date', label: 'Scheduled' },
  { field: 'total_items', label: 'Total Items' },
  { field: 'counted_items', label: 'Counted Items' },
  { field: 'total_variance', label: 'Variance' },
  { field: 'warehouse_type', label: 'Warehouse Type' },
  { field: 'city', label: 'City' },
  { field: 'total_quantity', label: 'Total Stock' },
  { field: 'total_value', label: 'Stock Value' },
  { field: 'alert_type', label: 'Alert Type' },
  { field: 'severity', label: 'Severity' },
  { field: 'current_quantity', label: 'Current Qty' },
  { field: 'maintenance_type', label: 'Maintenance Type' },
  { field: 'performed_date', label: 'Performed Date' },
  { field: 'performed_by', label: 'Performed By' },
  { field: 'hours_spent', label: 'Hours Spent' },
  { field: 'next_due_date', label: 'Next Due' },
];

// Inventory entity preset fields that are stored in custom_fields on the
// WorkItem. When sorting by one of these fields, the sort logic falls back to
// a custom_fields lookup when the top-level field is absent.
const INVENTORY_CUSTOM_FIELD_SORT_KEYS = new Set([
  'from_warehouse',
  'to_warehouse',
  'transfer_type',
  'expected_arrival_date',
  'product_name',
  'warehouse_name',
  'quantity',
  'fulfilled_quantity',
  'expires_at',
  'entity_type',
  'entity_id',
  'amount',
  'approvals_count',
  'requested_by_name',
  'list_type',
  'currency',
  'pricing_method',
  'item_count',
  'customer_count',
  'valid_from',
  'valid_to',
  'count_type',
  'scheduled_date',
  'total_items',
  'counted_items',
  'total_variance',
  'alert_type',
  'severity',
  'current_quantity',
  'maintenance_type',
  'performed_date',
  'performed_by',
  'hours_spent',
  'next_due_date',
]);

// Reads a sort value from custom_fields for inventory entity preset columns.
// Tries several common key variants (e.g. `from_warehouse_name` / `from_warehouse`).
function getCustomFieldSortValue(
  customFields: unknown,
  field: string,
): unknown {
  if (!customFields || typeof customFields !== 'object') return null;
  const cf = customFields as Record<string, unknown>;
  if (field in cf) return cf[field];
  // Try common alias variants
  const aliases: Record<string, string[]> = {
    from_warehouse: ['from_warehouse_name', 'from_warehouse_id'],
    to_warehouse: ['to_warehouse_name', 'to_warehouse_id'],
    product_name: ['item_name', 'product_title'],
    warehouse_name: ['warehouse'],
    fulfilled_quantity: ['quantity_fulfilled'],
    entity_id: ['entity_number'],
    amount: ['total_amount'],
    requested_by_name: ['requested_by'],
    item_count: ['total_items'],
    counted_items: ['items_counted'],
    total_variance: ['variance'],
  };
  for (const alias of aliases[field] || []) {
    if (alias in cf) return cf[alias];
  }
  return null;
}

// Priority order for sorting
const PRIORITY_ORDER: Record<string, number> = {
  'critical': 5,
  'urgent': 4,
  'high': 3,
  'medium': 2,
  'low': 1
};

// Status order for sorting
const STATUS_ORDER: Record<string, number> = {
  'open': 1,
  'pending': 2,
  'in_progress': 3,
  'review': 4,
  'testing': 5,
  'done': 6,
  'closed': 7,
  'archived': 8
};

const HEADER_STAT_EXCLUDED_STATUSES = new Set(['completed', 'closed', 'cancelled', 'resolved']);

function isHeaderStatActiveItem(item: WorkItem) {
  return !HEADER_STAT_EXCLUDED_STATUSES.has(item.status || '');
}

export interface ApplyWorkItemFilterRulesOptions {
  /**
   * When true, item type / subtype filters from the toolbar are skipped.
   * Used by schedule views to merge appointments/jobs back in while still
   * respecting date range, search, status, etc.
   */
  ignoreItemTypeFilters?: boolean;
  /** When true, skip the text search step (search was applied server-side). */
  skipSearch?: boolean;
}

/**
 * Pure filter pipeline shared with the hook and WorkItemsViewWrapper schedule merge.
 * Does not apply sorting.
 */
export function applyWorkItemFilterRules(
  items: WorkItem[],
  searchQuery: string,
  filters: FilterConfig,
  options?: ApplyWorkItemFilterRulesOptions
): WorkItem[] {
  let result = [...items];

  if (searchQuery && !options?.skipSearch) {
    const query = searchQuery.toLowerCase();

    const searchableValueMatches = (value: unknown): boolean => {
      if (value == null) return false;

      if (typeof value === 'string') {
        return value.toLowerCase().includes(query);
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value).toLowerCase().includes(query);
      }

      if (Array.isArray(value)) {
        return value.some((entry) => searchableValueMatches(entry));
      }

      if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).some((entry) => searchableValueMatches(entry));
      }

      return false;
    };

    result = result.filter((item) => {
      const dynamicItem = item as WorkItem & Record<string, unknown>;
      const assignees = Array.isArray(dynamicItem.assignees) ? dynamicItem.assignees : [];

      return [
        item.title,
        item.item_number,
        dynamicItem.doc_number,
        item.description,
        dynamicItem.notes,
        dynamicItem.internal_notes,
        item.contact_name,
        dynamicItem.contact_email,
        dynamicItem.contact_phone,
        dynamicItem.contact_person,
        dynamicItem.reporter_name,
        dynamicItem.reporter_email,
        dynamicItem.reporter_phone,
        dynamicItem.location_name,
        dynamicItem.location_address,
        item.item_type,
        item.item_subtype,
        item.status,
        item.priority,
        dynamicItem.assigned_to_name,
        dynamicItem.parent_job_number,
        dynamicItem.parent_title,
        dynamicItem.customer_po_number,
        dynamicItem.supplier_reference,
        dynamicItem.payment_terms,
      ].some((value) => searchableValueMatches(value))
        || searchableValueMatches(item.tags)
        || searchableValueMatches(dynamicItem.labels)
        || assignees.some((assignee) => searchableValueMatches([
          assignee.first_name,
          assignee.last_name,
          assignee.email,
        ]))
        || searchableValueMatches(dynamicItem.custom_fields);
    });
  }

  if (filters.status && filters.status.length > 0) {
    result = result.filter(item => {
      const status = item.status;
      return typeof status === 'string' && filters.status!.includes(status);
    });
  }

  if (filters.priority && filters.priority.length > 0) {
    result = result.filter(item => {
      const priority = item.priority;
      return typeof priority === 'string' && filters.priority!.includes(priority);
    });
  }

  if (!options?.ignoreItemTypeFilters) {
    if (filters.itemType && filters.itemType.length > 0) {
      result = result.filter(item => filters.itemType!.includes(item.item_type));
    }

    if (filters.itemSubtype && filters.itemSubtype.length > 0) {
      result = result.filter(item =>
        item.item_subtype && filters.itemSubtype!.includes(item.item_subtype)
      );
    }
  }

  if (filters.assignedTo && filters.assignedTo.length > 0) {
    result = result.filter(item => {
      const itemAssigneeIds: (number | null)[] = [];

      if (item.assignees && item.assignees.length > 0) {
        item.assignees.forEach(a => {
          if (a.user_id) itemAssigneeIds.push(a.user_id);
        });
      }

      const normalizedAssignedTo = typeof item.assigned_to === 'string'
        ? Number.parseInt(item.assigned_to, 10)
        : item.assigned_to;

      if (normalizedAssignedTo != null && !Number.isNaN(normalizedAssignedTo) && !itemAssigneeIds.includes(normalizedAssignedTo)) {
        itemAssigneeIds.push(normalizedAssignedTo);
      }

      if (itemAssigneeIds.length === 0) {
        itemAssigneeIds.push(null);
      }

      if (filters.assignedTo!.includes(null)) {
        return itemAssigneeIds.includes(null) ||
          itemAssigneeIds.some(id => id !== null && filters.assignedTo!.includes(id));
      }

      return itemAssigneeIds.some(id => id !== null && filters.assignedTo!.includes(id));
    });
  }

  if (filters.tags && filters.tags.length > 0) {
    result = result.filter(item =>
      item.tags && item.tags.some(tag => filters.tags!.includes(tag))
    );
  }

  if (filters.dateRange) {
    const { field, start, end } = filters.dateRange;
    result = result.filter(item => {
      const dateValue = item[field] ? new Date(item[field] as string) : null;
      if (!dateValue) return false;

      if (start && dateValue < start) return false;
      if (end && dateValue > end) return false;
      return true;
    });
  }

  const statCardFilter = filters.customFilters?.statCardFilter as WorkItemStatFilter | undefined;
  if (statCardFilter) {
    const now = new Date();

    result = result.filter((item) => {
      switch (statCardFilter) {
        case 'open':
          return isHeaderStatActiveItem(item);
        case 'urgent':
          return isHeaderStatActiveItem(item) && (item.priority === 'urgent' || item.priority === 'critical');
        case 'in_progress':
          return item.status === 'in_progress' || item.status === 'dispatched';
        case 'overdue':
          return isHeaderStatActiveItem(item)
            && Boolean(item.due_date)
            && new Date(item.due_date as string) < now;
        case 'unassigned': {
          if (!isHeaderStatActiveItem(item)) {
            return false;
          }

          const hasAssignees = (Array.isArray(item.assignees) && item.assignees.some((assignee) => Boolean(assignee.user_id)))
            || Boolean(item.assigned_to);

          return !hasAssignees;
        }
        case 'on_hold':
          return item.status === 'on_hold';
        default:
          return true;
      }
    });
  }

  const financeRecurringOnly = filters.customFilters?.finance_recurring_only === true;
  if (financeRecurringOnly) {
    result = result.filter((item) => invoiceRowIsRecurring(item as never));
  }

  const requestedDocType = filters.customFilters?.requestedDocType as string | undefined;
  if (requestedDocType) {
    result = result.filter((item) => {
      const subtype = String(item.item_subtype || '').toLowerCase();
      if (subtype === requestedDocType) return true;

      const cf = item.custom_fields;
      if (cf && typeof cf === 'object') {
        const rdt = String((cf as Record<string, unknown>).requested_doc_type || '').toLowerCase();
        if (rdt === requestedDocType) return true;
      }

      return false;
    });
  }

  if (filters === DEFAULT_PRESETS.find(p => p.id === 'overdue')?.filters) {
    const now = new Date();
    result = result.filter(item => {
      if (!item.due_date) return false;
      const dueDate = new Date(item.due_date);
      return dueDate < now && !['done', 'closed', 'archived'].includes(item.status || '');
    });
  }

  return result;
}

function countActiveCustomFilters(customFilters?: Record<string, unknown>) {
  if (!customFilters) return 0;

  return Object.values(customFilters).filter((value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  }).length;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useWorkItemsFilter({
  items,
  defaultFilters = {},
  defaultSort = [{ field: 'created_at', direction: 'desc' }],
  onFilterChange,
  onSortChange,
  serverSideSearch = false,
  serverResultTotal = null,
}: UseWorkItemsFilterOptions): UseWorkItemsFilterReturn {
  // State
  const [filters, setFiltersState] = useState<FilterConfig>(defaultFilters);
  const [searchQuery, setSearchQueryState] = useState<string>(defaultFilters.search || '');
  const [sorts, setSortsState] = useState<SortConfig[]>(defaultSort);
  const [presets, setPresets] = useState<FilterPreset[]>(DEFAULT_PRESETS);

  // Update filters
  const setFilters = useCallback((newFilters: FilterConfig) => {
    setFiltersState(newFilters);
    if (newFilters.search !== undefined) {
      setSearchQueryState(newFilters.search);
    }
    onFilterChange?.(newFilters);
  }, [onFilterChange]);

  // Update single filter
  const updateFilter = useCallback(<K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  }, [filters, setFilters]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchQueryState('');
  }, [setFilters]);

  // Set search query
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
    setFiltersState(prev => ({ ...prev, search: query }));
    onFilterChange?.({ ...filters, search: query });
  }, [filters, onFilterChange]);

  // Set sorts
  const setSorts = useCallback((newSorts: SortConfig[]) => {
    setSortsState(newSorts);
    onSortChange?.(newSorts);
  }, [onSortChange]);

  // Toggle sort on a field
  const toggleSort = useCallback((field: string) => {
    const existingSort = sorts.find(s => s.field === field);
    
    if (existingSort) {
      if (existingSort.direction === 'asc') {
        // Change to desc
        const newSorts = sorts.map(s => 
          s.field === field ? { ...s, direction: 'desc' as SortDirection } : s
        );
        setSorts(newSorts);
      } else {
        // Remove sort
        const newSorts = sorts.filter(s => s.field !== field);
        setSorts(newSorts.length > 0 ? newSorts : defaultSort);
      }
    } else {
      // Add as primary sort
      setSorts([{ field, direction: 'asc' }, ...sorts.filter(s => s.field !== field)]);
    }
  }, [sorts, setSorts, defaultSort]);

  // Clear sorts
  const clearSorts = useCallback(() => {
    setSorts(defaultSort);
  }, [setSorts, defaultSort]);

  // Apply preset
  const applyPreset = useCallback((presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setFilters(preset.filters);
      setSorts(preset.sorts);
    }
  }, [presets, setFilters, setSorts]);

  // Save current state as preset
  const savePreset = useCallback((name: string, icon?: string): FilterPreset => {
    const newPreset: FilterPreset = {
      id: `custom-${Date.now()}`,
      name,
      filters: { ...filters },
      sorts: [...sorts],
      icon: icon || '📌'
    };
    setPresets(prev => [...prev, newPreset]);
    return newPreset;
  }, [filters, sorts]);

  // Quick filters
  const isHighPriorityActive = useMemo(() => {
    return (
      filters.priority?.length === HIGH_PRIORITY_VALUES.length &&
      HIGH_PRIORITY_VALUES.every(v => filters.priority?.includes(v))
    );
  }, [filters.priority]);

  const quickFilters = useMemo(() => ({
    myItems: () => {
      // Note: This should be called with userId from context
      applyPreset('my-items');
    },
    overdue: () => {
      applyPreset('overdue');
    },
    highPriority: () => {
      if (isHighPriorityActive) {
        // Toggle off: remove priority filter, keep other filters intact
        const { priority: _priority, ...restFilters } = filters;
        setFilters(restFilters);
        clearSorts();
      } else {
        // Toggle on: add priority filter, keep other filters intact
        setFilters({ ...filters, priority: HIGH_PRIORITY_VALUES });
        setSorts([{ field: 'priority', direction: 'desc' }, { field: 'due_date', direction: 'asc' }]);
      }
    },
    recentlyUpdated: () => {
      applyPreset('recently-updated');
    },
    unassigned: () => {
      setFilters({ ...filters, assignedTo: [null] });
    }
  }), [applyPreset, filters, setFilters, isHighPriorityActive, clearSorts, setSorts]);

  // Filter items
  const filteredItems = useMemo(() => {
    const result = applyWorkItemFilterRules(items, searchQuery, filters, {
      // When serverSideSearch is true, skip client-side text search because
      // the API already filtered by the search param.  Instant feedback is
      // handled by the optimisticClientFilterItems mechanism in
      // WorkItemsViewWrapper, which computes client-filtered results with
      // skipSearch: false on the current page for immediate display.
      skipSearch: serverSideSearch,
    });

    // Apply sorts
    if (sorts.length > 0) {
      result.sort((a, b) => {
        for (const sort of sorts) {
          // Inventory entity preset fields are stored in custom_fields or
          // joined as top-level fields on mapped rows (warehouses).
          // Fall back to custom_fields lookup when the top-level field is absent.
          let aVal: unknown = a[sort.field as keyof WorkItem];
          let bVal: unknown = b[sort.field as keyof WorkItem];
          if (aVal == null) {
            aVal = getCustomFieldSortValue(a.custom_fields, sort.field);
          }
          if (bVal == null) {
            bVal = getCustomFieldSortValue(b.custom_fields, sort.field);
          }

          // Handle null/undefined
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return sort.direction === 'asc' ? 1 : -1;
          if (bVal == null) return sort.direction === 'asc' ? -1 : 1;

          let comparison = 0;

          // Special handling for priority
          if (sort.field === 'priority') {
            const aOrder = PRIORITY_ORDER[aVal as string] || 0;
            const bOrder = PRIORITY_ORDER[bVal as string] || 0;
            comparison = aOrder - bOrder;
          }
          // Special handling for status
          else if (sort.field === 'status') {
            const aOrder = STATUS_ORDER[aVal as string] || 0;
            const bOrder = STATUS_ORDER[bVal as string] || 0;
            comparison = aOrder - bOrder;
          }
          // Date comparison (includes inventory date fields like expected_arrival_date,
          // expires_at, valid_from, valid_to, scheduled_date)
          else if (sort.field.includes('date') || sort.field.includes('_at') || sort.field === 'expires_at' || sort.field === 'valid_from' || sort.field === 'valid_to' || sort.field === 'scheduled_date' || sort.field === 'expected_arrival_date') {
            const aDate = new Date(aVal as string).getTime();
            const bDate = new Date(bVal as string).getTime();
            comparison = aDate - bDate;
          }
          // Number comparison (includes inventory numeric fields)
          else if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal;
          }
          // String comparison
          else {
            comparison = String(aVal).localeCompare(String(bVal));
          }

          if (comparison !== 0) {
            return sort.direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    return result;
  }, [items, searchQuery, filters, sorts, serverSideSearch]);

  // Calculate stats
  const stats = useMemo(() => {
    const byStatus: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    const byType: Record<string, number> = {};
    const bySubtype: Record<string, number> = {};

    items.forEach(item => {
      // Status counts
      if (item.status) {
        byStatus[item.status] = (byStatus[item.status] || 0) + 1;
      }
      
      // Priority counts
      if (item.priority) {
        byPriority[item.priority] = (byPriority[item.priority] || 0) + 1;
      }
      
      // Type counts
      byType[item.item_type] = (byType[item.item_type] || 0) + 1;

      // Subtype counts
      if (item.item_subtype) {
        bySubtype[item.item_subtype] = (bySubtype[item.item_subtype] || 0) + 1;
      }
    });

    return {
      total: serverResultTotal != null ? serverResultTotal : items.length,
      filtered: filteredItems.length,
      byStatus,
      byPriority,
      byType,
      bySubtype
    };
  }, [items, filteredItems, serverResultTotal]);

  // Check for active filters
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchQuery ||
      (filters.status && filters.status.length > 0) ||
      (filters.priority && filters.priority.length > 0) ||
      (filters.itemType && filters.itemType.length > 0) ||
      (filters.itemSubtype && filters.itemSubtype.length > 0) ||
      (filters.assignedTo && filters.assignedTo.length > 0) ||
      (filters.tags && filters.tags.length > 0) ||
      filters.dateRange ||
      countActiveCustomFilters(filters.customFilters) > 0
    );
  }, [searchQuery, filters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.priority && filters.priority.length > 0) count++;
    if (filters.itemType && filters.itemType.length > 0) count++;
    if (filters.itemSubtype && filters.itemSubtype.length > 0) count++;
    if (filters.assignedTo && filters.assignedTo.length > 0) count++;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.dateRange) count++;
    count += countActiveCustomFilters(filters.customFilters);
    return count;
  }, [searchQuery, filters]);

  return {
    filteredItems,
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    searchQuery,
    setSearchQuery,
    sorts,
    setSorts,
    toggleSort,
    clearSorts,
    presets,
    applyPreset,
    savePreset,
    quickFilters,
    isHighPriorityActive,
    stats
  };
}

export default useWorkItemsFilter;
